import type { Point, Stroke, StrokeInput, AnimatedDrawing } from '@twitch-draw/shared';
import type { OnionSkinData } from '../hooks/useDrawingState';

export function renderStroke(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  color: string,
  width: number,
  canvasWidth: number,
  canvasHeight: number,
): void {
  if (points.length < 2) return;

  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const [startX, startY] = points[0];
  ctx.moveTo(startX * canvasWidth, startY * canvasHeight);

  points.slice(1).forEach(([x, y]) => {
    ctx.lineTo(x * canvasWidth, y * canvasHeight);
  });

  ctx.stroke();
}

function renderDisplayNameLabel(
  ctx: CanvasRenderingContext2D,
  displayName: string,
  x: number,
  y: number,
): void {
  const label = `@${displayName}`;
  ctx.font = '600 11px -apple-system, BlinkMacSystemFont, sans-serif';
  const metrics = ctx.measureText(label);
  const padding = 4;
  const height = 16;
  const bgX = x - padding;
  const bgY = y - height - 4;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.beginPath();
  ctx.roundRect(bgX, bgY, metrics.width + padding * 2, height, 4);
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.fillText(label, x, bgY + 12);
}

export function getAnimationFrameIndex(animation: AnimatedDrawing): number {
  const now = Date.now();
  const frameInterval = 1000 / animation.fps;
  return Math.floor(now / frameInterval) % animation.frames.length;
}

function findTopmostPoint(points: Point[]): Point {
  let minY = points[0][1];
  let minX = points[0][0];
  for (const [x, y] of points) {
    if (y < minY || (y === minY && x < minX)) {
      minY = y;
      minX = x;
    }
  }
  return [minX, minY];
}

const LABEL_GAP = 12;

function renderAnimatedStrokeBadge(
  ctx: CanvasRenderingContext2D,
  stroke: Stroke,
  canvasWidth: number,
  canvasHeight: number,
  labelledUsers: Set<string>,
): void {
  if (!stroke.displayName || labelledUsers.has(stroke.userId) || !stroke.animation) return;
  labelledUsers.add(stroke.userId);
  const allPoints = stroke.animation.frames.flatMap(frame =>
    frame.flatMap(s => s.points),
  );
  if (allPoints.length === 0) return;
  const [px, py] = findTopmostPoint(allPoints);
  renderDisplayNameLabel(ctx, stroke.displayName, px * canvasWidth, py * canvasHeight - LABEL_GAP);
}

function renderStaticStrokeBadge(
  ctx: CanvasRenderingContext2D,
  stroke: Stroke,
  canvasWidth: number,
  canvasHeight: number,
  labelledUsers: Set<string>,
): void {
  if (!stroke.displayName || labelledUsers.has(stroke.userId)) return;
  labelledUsers.add(stroke.userId);
  const [px, py] = findTopmostPoint(stroke.points);
  renderDisplayNameLabel(ctx, stroke.displayName, px * canvasWidth, py * canvasHeight - LABEL_GAP);
}

export function renderStrokes(
  ctx: CanvasRenderingContext2D,
  strokes: Stroke[],
  canvasWidth: number,
  canvasHeight: number,
): boolean {
  const labelledUsers = new Set<string>();
  let hasAnimations = false;

  strokes.forEach((stroke) => {
    if (!stroke.animation || stroke.animation.frames.length <= 1) {
      renderStroke(ctx, stroke.points, stroke.color, stroke.width, canvasWidth, canvasHeight);
      renderStaticStrokeBadge(ctx, stroke, canvasWidth, canvasHeight, labelledUsers);
      return;
    }

    hasAnimations = true;
    const frameIdx = getAnimationFrameIndex(stroke.animation);
    const frameStrokes = stroke.animation.frames[frameIdx] ?? [];
    frameStrokes.forEach((fs) =>
      renderStroke(ctx, fs.points, fs.color, fs.width, canvasWidth, canvasHeight),
    );
    renderAnimatedStrokeBadge(ctx, stroke, canvasWidth, canvasHeight, labelledUsers);
  });

  return hasAnimations;
}

export function renderCurrentStroke(
  ctx: CanvasRenderingContext2D,
  stroke: StrokeInput | null,
  canvasWidth: number,
  canvasHeight: number,
): void {
  if (!stroke) return;
  renderStroke(ctx, stroke.points, stroke.color, stroke.width, canvasWidth, canvasHeight);
}

export function clearCanvas(ctx: CanvasRenderingContext2D): void {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}

export function renderFrameStrokes(
  ctx: CanvasRenderingContext2D,
  strokes: StrokeInput[],
  canvasWidth: number,
  canvasHeight: number,
  opacity: number,
): void {
  ctx.save();
  ctx.globalAlpha = opacity;
  strokes.forEach((stroke) =>
    renderStroke(ctx, stroke.points, stroke.color, stroke.width, canvasWidth, canvasHeight),
  );
  ctx.restore();
}

export function renderOnionSkin(
  ctx: CanvasRenderingContext2D,
  onionSkin: OnionSkinData,
  canvasWidth: number,
  canvasHeight: number,
): void {
  const { frames, activeIndex, isPreviewing } = onionSkin;

  if (isPreviewing) {
    renderFrameStrokes(ctx, frames[activeIndex] ?? [], canvasWidth, canvasHeight, 1.0);
    return;
  }

  frames
    .filter((_, i) => i !== activeIndex)
    .forEach((frameStrokes) =>
      renderFrameStrokes(ctx, frameStrokes, canvasWidth, canvasHeight, 0.3),
    );

  if (activeIndex >= 0 && activeIndex < frames.length) {
    renderFrameStrokes(ctx, frames[activeIndex], canvasWidth, canvasHeight, 1.0);
  }
}

export function normalizeCoordinates(
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement,
): Point {
  const rect = canvas.getBoundingClientRect();
  const x = (clientX - rect.left) / rect.width;
  const y = (clientY - rect.top) / rect.height;

  return [
    Math.max(0, Math.min(1, x)),
    Math.max(0, Math.min(1, y)),
  ];
}
